import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  ChoiceList,
  Spinner,
} from '@shopify/polaris';
import {
  usePinterestConfig,
  useConfigurePinterest,
  useTogglePinterest,
  useTestPinterest,
  useDisconnectPinterest,
  useConnectPinterest,
  usePinterestAssets,
  useSelectPinterestAsset,
} from '../hooks/api';
import { useLanguage, useTranslation } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { openOAuthWindow, navigateOAuth } from '../lib/auth';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';
import { GateBanner, type PageError } from '../components/GateBanner';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'connected': return <Badge tone="success">Connected</Badge>;
    case 'warning': return <Badge tone="warning">Warning</Badge>;
    case 'error': return <Badge tone="critical">Error</Badge>;
    case 'disabled': return <Badge>Disabled</Badge>;
    default: return <Badge>{status}</Badge>;
  }
}

export function Pinterest() {
  const { data: config, isLoading } = usePinterestConfig();
  const configureMutation = useConfigurePinterest();
  const toggleMutation = useTogglePinterest();
  const testMutation = useTestPinterest();
  const disconnectMutation = useDisconnectPinterest();
  const { language } = useLanguage();
  const t = useTranslation();

  const connectMutation = useConnectPinterest();
  const selectAssetMutation = useSelectPinterestAsset();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [conversionToken, setConversionToken] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<PageError>(null);
  const [actionError, setActionError] = useState<PageError>(null);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);
  const [connectError, setConnectError] = useState<PageError>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      setConnectError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The Pinterest connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The Pinterest connection could not be completed (${oauthErr.replace(/^[a-z0-9_]*?_/, '')}). Please try again.`
      );
    }
    if (params.get('step') === 'select-account' || params.get('pinterest') === 'connected') {
      setShowPicker(true);
    }
  }, []);

  const assetsQuery = usePinterestAssets(showPicker);

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    const win = openOAuthWindow();
    try {
      const { url } = await connectMutation.mutateAsync();
      navigateOAuth(win, url);
    } catch (error: any) {
      win?.close();
      setConnectError(error?.message ? error : 'Could not start the Pinterest connection.');
    }
  }, [connectMutation]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedAccount('');
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    params.delete('pinterest');
    params.delete('platform');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }, []);

  const handleSelectAsset = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      await selectAssetMutation.mutateAsync({ adAccountId: selectedAccount });
      closePicker();
    } catch (error: any) {
      setConnectError(error?.message ? error : 'Could not finish the connection.');
    }
  }, [selectedAccount, selectAssetMutation, closePicker]);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    if (!adAccountId || !conversionToken) {
      setConfigError('Ad Account ID and Conversion Token are required');
      return;
    }
    try {
      await configureMutation.mutateAsync({ adAccountId, conversionToken });
      setShowConfigModal(false);
      setAdAccountId('');
      setConversionToken('');
    } catch (error: any) {
      setConfigError(error?.message ? error : t.common.configFailed);
    }
  }, [adAccountId, conversionToken, configureMutation]);

  const handleToggle = useCallback(async (enabled: boolean) => {
    setActionError(null);
    try { await toggleMutation.mutateAsync(enabled); }
    catch (error: any) { setActionError(error?.message ? error : t.common.actionFailed); }
  }, [toggleMutation]);

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      await testMutation.mutateAsync();
      setTestResult({ success: true, message: 'Test event sent successfully' });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Test failed' });
    }
  }, [testMutation]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error: any) { setActionError(error?.message ? error : 'Disconnect failed'); }
  }, [disconnectMutation]);

  if (isLoading) {
    return (
      <Page title="Pinterest Conversions API">
        <Layout><Layout.Section><Card><SkeletonBodyText lines={6} /></Card></Layout.Section></Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Pinterest Conversions API"
      subtitle="Server-side conversion tracking for Pinterest Ads"
      primaryAction={config?.connected ? {
        content: config.isEnabled ? 'Disable Tracking' : 'Enable Tracking',
        onAction: () => handleToggle(!config.isEnabled),
        loading: toggleMutation.isPending,
      } : undefined}
    >
      <Layout>
        {actionError && (
          <Layout.Section>
            <GateBanner error={actionError} onDismiss={() => setActionError(null)} />
          </Layout.Section>
        )}

        {connectError && (
          <Layout.Section>
            <GateBanner error={connectError} onDismiss={() => setConnectError(null)} />
          </Layout.Section>
        )}

        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title="Warning"><p>{config.lastErrorMessage}</p></Banner>
          </Layout.Section>
        )}

        {testResult && (
          <Layout.Section>
            <Banner tone={testResult.success ? 'success' : 'critical'} onDismiss={() => setTestResult(null)}>
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Connection Status</Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">Ad Account ID:</Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">{config.adAccountId}</Text>
                  </InlineStack>

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">Last sync:</Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>Send Test Event</Button>
                    <Button onClick={() => setShowConfigModal(true)}>Update Configuration</Button>
                    <Button variant="plain" tone="critical" onClick={() => setShowDisconnectModal(true)}>
                      Disconnect
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  {/* Only offer one-click when the Pinterest developer app is
                      actually configured. Before this, the button was always
                      shown and sent the merchant to Pinterest with an empty
                      client_id, landing them on Pinterest's error page. */}
                  <Text as="p" variant="bodyMd">
                    {config?.oauthAvailable
                      ? 'Connect Pinterest in one click - we list your ad accounts and set up the Conversions API for you, no account ID or token to copy.'
                      : 'Add your Pinterest ad account ID and conversion token to start sending server-side conversions.'}
                  </Text>
                  <InlineStack gap="200">
                    {config?.oauthAvailable ? (
                      <>
                        <Button variant="primary" onClick={handleConnect} loading={connectMutation.isPending}>
                          Connect Pinterest
                        </Button>
                        <Button onClick={() => setShowConfigModal(true)}>
                          Advanced: enter manually
                        </Button>
                      </>
                    ) : (
                      <Button variant="primary" onClick={() => setShowConfigModal(true)}>
                        Set up Pinterest
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Manual-path instructions ONLY when one-click is unavailable. They tell
            the merchant to generate and paste a Conversion Access Token, which is
            the opposite of what happens on the OAuth path - and Pinterest reviews
            Standard-access apps specifically for "proper OAuth authentication
            methods, not alternative credential collection approaches". Showing
            both at once contradicted the primary CTA sitting right above it. The
            manual steps still live in the "Advanced: enter manually" modal. */}
        {!config?.connected && !config?.oauthAvailable && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Setup Instructions</Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">1. Go to Pinterest Ads Manager and find your Ad Account ID</Text>
                  <Text as="p" variant="bodyMd">2. Navigate to Conversions {">"} Set up the Conversions API</Text>
                  <Text as="p" variant="bodyMd">3. Generate a Conversion Access Token</Text>
                  <Text as="p" variant="bodyMd">4. Enter your credentials below and click Connect</Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        <Modal
          open={showConfigModal}
          onClose={() => { setShowConfigModal(false); setConfigError(null); }}
          title="Configure Pinterest Conversions API"
          primaryAction={{
            content: 'Save Configuration',
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[{
            content: 'Cancel',
            onAction: () => { setShowConfigModal(false); setConfigError(null); },
          }]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && <GateBanner error={configError} />}
              <TextField
                label="Ad Account ID"
                value={adAccountId}
                onChange={setAdAccountId}
                placeholder="123456789"
                autoComplete="off"
                helpText="Found in your Pinterest Ads Manager settings"
              />
              <TextField
                label="Conversion Access Token"
                value={conversionToken}
                onChange={setConversionToken}
                type="password"
                placeholder="pina_..."
                autoComplete="off"
                helpText="Generate in Pinterest Conversions API settings"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Ad-account Picker Modal (after one-click Connect) */}
        <Modal
          open={showPicker}
          onClose={closePicker}
          title="Choose your Pinterest ad account"
          primaryAction={{
            content: 'Finish setup',
            onAction: handleSelectAsset,
            loading: selectAssetMutation.isPending,
            disabled: !selectedAccount,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: closePicker }]}
        >
          <Modal.Section>
            {assetsQuery.isLoading ? (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" accessibilityLabel="Loading ad accounts" />
                <Text as="span" variant="bodyMd">Loading your Pinterest ad accounts...</Text>
              </InlineStack>
            ) : assetsQuery.isError ? (
              <Banner tone="critical">
                <p>Could not load your ad accounts. The connection may have expired - click Connect again.</p>
              </Banner>
            ) : (assetsQuery.data?.accounts.length ?? 0) === 0 ? (
              <Banner tone="warning">
                <p>No Pinterest ad accounts found on this account.</p>
              </Banner>
            ) : (
              <ChoiceList
                title="Pick the ad account to send conversions to."
                choices={(assetsQuery.data?.accounts ?? []).map((a) => ({
                  label: `${a.name} (${a.adAccountId})`,
                  value: a.adAccountId,
                }))}
                selected={selectedAccount ? [selectedAccount] : []}
                onChange={(v) => setSelectedAccount(v[0] ?? '')}
              />
            )}
          </Modal.Section>
        </Modal>

        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title="Disconnect Pinterest"
          primaryAction={{
            content: 'Disconnect',
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setShowDisconnectModal(false) }]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              This will stop sending conversion events to Pinterest. You can reconnect at any time.
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
